import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { lpcoEnvs } from "@/tz/go/egaz/osci/lpco/comn";
import { colUtils } from "@/tz/go/egaz/osci/colt/comn";

/*
 * Receipt Management Service
 * !== 수납 관리 서비스 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 경로 Array
 */
export const BASE = {
    path: `${lpcoEnvs.base}/colt/rcpt`,
    api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/rcpt`,
    nodes: [
        { path: "/", label: "Collection" },
        { path: "/colt", label: "Notice" },
        { path: "/colt/rcpt", label: "Receipt" },
    ],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    osci0401002q: `/osci/colt/ntfc/osci0401002q`, // Bill Detail !== 고지서 상세 ==!
    osci0401003q: `/osci/colt/ntfc/osci0401003q`, // TANSAD Bill List !== TANSAD 고지서 목록 ==!
    osci0402001q: `/osci/colt/rcpt/osci0402001q`, // Receipt List !== 수납 목록 ==!
    osci0402002q: `/osci/colt/rcpt/osci0402002q`, // Receipt Detail !== 수납 상세 ==!
    osci0402003q: `/osci/colt/rcpt/osci0402003q`, // Non-Receipt List !== 미수납 목록 ==!
    osci0402004q: `/osci/colt/rcpt/osci0402004q`, // Receipt Error List !== 수납 오류 목록 ==!
    osci0402005q: `/osci/colt/rcpt/osci0402005q`, // Manual Receipt List !== 수작업 수납 목록 ==!
    osci0402006q: `/osci/colt/rcpt/osci0402006q`, // Manual Receipt Detail !== 수작업 수납 상세 ==!
    osci0402007s: `/osci/colt/rcpt/osci0402007s`, // Manual Receipt Registration !== 수작업 수납 등록 ==!
    osci0402008q: `/osci/colt/rcpt/osci0402008q`, // Pay Discrepancy !== 납부 불일치 목록 ==!
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    // Get Receipt List !== 수납 목록 조회 ==!
    getRcptList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/rcptLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Non-Receipt List !== 미수납 목록 조회 ==!
    getNrcptList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/nrcptLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Receipt Error List !== 수납 오류 목록 조회 ==!
    getRcptErrList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/rcptErrLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Manual Receipt List !== 수작업 수납 목록 조회 ==!
    getManlRcptList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/manlRcptLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Manual Receipt Detail !== 수작업 수납 상세 조회 ==!
    getManlRcpt: (id: any) => {
        return api.get(`${BASE.api}/${id}/manlRcptDtl`);
    },
    // Get Manual Receipt Detail !== 수작업 수납 상세 조회 ==!
    getManlRcptDtl: (data: any) => {
        return api.get(`${BASE.api}/manlRcptDtl`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Save Manual Receipt !== 수작업 수납 저장 ==!
    saveManlRcpt: (data: any) => {
        return api.post(`${BASE.api}/manlRcptSave`, {
            ...data,
        });
    },
    // Get Manual Receipt Detail for Save !== 저장을 위한 수작업 수납 상세 조회 ==!
    getManlRcptForSave: (id: any) => {
        return api.get(`${BASE.api}/${id}/manlRcptDtlForSave`);
    },
    getManlRcptDtlForSave: (data: any) => {
        return api.get(`${BASE.api}/manlRcptDtlForSave`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Pay Discrepancy List !== 납부 불일치 목록 조회 ==!
    getPayDscrList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/payDscrLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Oil Tex Receipt List !== 오일세 수납 목록 조회 ==!
    getOilRcptList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/oilRcptLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Industrial Sugar Tex Receipt List !== 공업용 설탕세 수납 목록 조회 ==!
    getIdstSugrRcptList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/idstSugrRcptLst?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Manual Receipt Approval Request After !== 수작업 수납 결재 상신 후처리 ==!
    manlRcptAprvRqstAf: (data: any) => {
        return api.post(`${BASE.api}/manlRcptAprvRqstAf`, {
            ...data,
        });
    },
    manlRcptAprvAf: (data: any) => {
        return api.post(`${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/comn/aprv-af/manlRcptAprvAf`, {
            ...data,
        });
    },
    updateLpcoFeePayment: (data: any) => {
        console.log(data);
        return api.get(`${process.env.REACT_APP_API_OSCI}/api/v1/eswe/comn/test-fee-pay/${data}`);
    },
};

/*
 * @ 그리드 스키마 정의
 * @ 화면에서 사용하는 그리드 구조 정의
 * @ SG_ 로 시작 (Schema grid)
 * @ SG_[그리드명과 조회하는 데이터 이름 메타 단어(용어) 대문자, "_" 로 구분 , 그리드명 맨 뒤에 "_LIST" 로 종료]
 */

// Schema of Receipt List Grid !== 수납 목록 그리드 스키마 ==!
export const SG_RCPT_LIST: TGridSchema = {
    id: "grid_rcptList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160 }], id: "bllNo" },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 130 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 100 }] },
        { cells: [{ header: "L_RCPT_NO", binding: "rcptNo", width: "*" }] },
        { cells: [{ header: "L_RCPT_DT", binding: "rcptDt", width: 100 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 100 }] },
        { cells: [{ header: "L_DCLT", binding: "dcltTin", width: 100 }] },
        // { cells: [{ header: "L_RCPT_BNK", binding: "rcptBnkCd", width: 120 }] },
        // { cells: [{ header: "L_RCPT_BNK_ACCT", binding: "rcptBnkAcctNo", width: 150 }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: 120 }] },
        // { cells: [{ header: "L_PAY_METD", binding: "payMetdCd", width: 120 }] },
        { cells: [{ header: "L_RCPT_USR", binding: "rcptRgsrOfcrId", width: "*" }] },
        // { cells: [{ header: "L_TRSC_ID", binding: "zmpTrscId", width: 150 }] },
        // { cells: [{ header: "L_PAY_REFF_ID", binding: "payreffId", width: 150 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "rcptNo" }] },
        { cells: [{ binding: "rcptDt", type: "date" }] },
        { cells: [{ binding: "pyerMgmtNo", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "dcltTin", mask: colUtils.getMaskPattern("tin") }] },
        // { cells: [{ binding: "rcptBnkCd" }] },
        // { cells: [{ binding: "rcptBnkAcctNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        // { cells: [{ binding: "payMetdCd", area: "comnCd", comnCd: "OSC0065", type: "code" }] },
        { cells: [{ binding: "rcptRgsrOfcrId" }] },
        // { cells: [{ binding: "zmpTrscId" }] },
        // { cells: [{ binding: "payReffId" }] },
    ],
};

// Schema of Non-Receipt List Grid !== 미수납 목록 그리드 스키마 ==!
export const SG_NRCPT_LIST: TGridSchema = {
    id: "grid_nrcptList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 120 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 130 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 120 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 120 }] },
        { cells: [{ header: "L_DCLT", binding: "dcltTin", width: 120 }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 120 }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: 120 }] },
        // { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: 120 }] },
        { cells: [{ header: "L_STAT", binding: "dlqnYn", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "pyerMgmtNo", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "dcltTin", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        // { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "dlqnYn", area: "comnCd", comnCd: "OSC0059", type: "code" }] },
    ],
};

// Schema of Receipt Error List Grid !== 수납 오류 목록 그리드 스키마 ==!
export const SG_RCPT_ERR_LIST: TGridSchema = {
    id: "grid_rcptErrList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 200 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 120 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 200 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 120 }] },
        { cells: [{ header: "L_ERR_RCVE_DT", binding: "errRcveDt", width: 120 }] },
        { cells: [{ header: "L_PAY_BNK", binding: "payBnkCd", width: 150 }] },
        { cells: [{ header: "L_PAY_BNK_ACCT", binding: "payBnkAcctNo", width: 200 }] },
        { cells: [{ header: "L_ERR_DESC", binding: "errMsgCn", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "errRcveDt", type: "date" }] },
        { cells: [{ binding: "payBnkCd", area: "bnkCd", type: "code" }] },
        { cells: [{ binding: "payBnkAcctNo" }] },
        { cells: [{ binding: "errMsgCn" }] },
    ],
};

// Schema of Manual Receipt List Grid !== 수작업 수납 목록 그리드 스키마 ==!
export const SG_MANL_RCPT_LIST: TGridSchema = {
    id: "grid_manlRcptList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: "*" }], id: "bllNo" },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 200 }] },
        // { cells: [{ header: "L_SRNO", binding: "bllRcptSrno", width: "*" }] },
        { cells: [{ header: "L_RGSR_DT", binding: "rcptRgsrDt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_DT", binding: "rcptDt", width: "*" }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: "*" }] },
        { cells: [{ header: "L_RGSR", binding: "rcptRgsrOfcrNm", width: "*" }] },
        // { cells: [{ header: "L_SBMR", binding: "sbmrNm", width: "*" }] },
        // { cells: [{ header: "L_SBMS_DT", binding: "sbmsDt", width: 120 }] },
        // { cells: [{ header: "L_APRV_STAT_CD", binding: "aprvPrcssStatCd", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        // { cells: [{ binding: "bllRcptSrno" }] },
        { cells: [{ binding: "rcptRgsrDt", type: "datetime" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptDt", type: "datetime" }] },
        { cells: [{ binding: "pyerMgmtNo" }] },
        { cells: [{ binding: "rcptRgsrOfcrNm" }] },
        // { cells: [{ binding: "sbmrNm" }] },
        // { cells: [{ binding: "sbmsDt" }] },
        // { cells: [{ binding: "aprvPrcssStatCd", type: "code", area: "comnCd", comnCd: "OSC0096" }] },
    ],
};

// Schema of Pay Discrepancy List Grid !== 납부 불일치 목록 그리드 스키마 ==!
export const SG_PAY_DSCR_LIST: TGridSchema = {
    id: "grid_payDscrList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160, rowspan: 2 }], id: "bllNo" },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 160, rowspan: 2 }] },
        {
            colspan: 2,
            cells: [
                { header: "L_BLL", colspan: 2 },
                { header: "L_DT", binding: "bllDt", width: 130 },
                { header: "L_AMT", binding: "feeAmt", width: "*" },
            ],
        },
        {
            colspan: 2,
            cells: [
                { header: "L_RCPT", colspan: 2 },
                { header: "L_DT", binding: "rcptDt", width: 130 },
                { header: "L_AMT", binding: "rcptAmt", width: "*" },
            ],
        },
        { cells: [{ header: "L_DSCR_TP", binding: "dscrTpCd", width: 150, rowspan: 2 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptDt", type: "date" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "dscrTpCd", area: "comnCd", comnCd: "OSC0066", type: "code" }] },
    ],
};

// Schema of Oil Tex Receipt List Grid !== 오일세 수납 목록 그리드 스키마 ==!
export const SG_OIL_RCPT_LIST: TGridSchema = {
    id: "grid_oilRcptList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: 120 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 140 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 100 }] },
        { cells: [{ header: "L_RCPT_NO", binding: "rcptNo", width: 130 }] },
        { cells: [{ header: "L_RCPT_DT", binding: "rcptDt", width: 100 }] },
        { cells: [{ header: "L_TAX", binding: "totBllTaxAmt", width: 120 }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: 120 }] },
        { cells: [{ header: "L_RCPT_BNK", binding: "rcptBnkCd", width: 120 }] },
        { cells: [{ header: "L_RCPT_BNK_ACCT_NO", binding: "rcptBnkAcctNo", width: 120 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 100 }] },
        { cells: [{ header: "L_PYER_NM", binding: "pyerNm", width: 200 }] },
        { cells: [{ header: "L_DCLT_TIN", binding: "dcltTin", width: 100 }] },
        { cells: [{ header: "L_DCLT_NM", binding: "dcltNm", width: 200 }] },
        { cells: [{ header: "L_BLL_TAX_AMT", binding: "totAmt", width: 120 }] },
        { cells: [{ header: "L_PAY_METD", binding: "payMetdCd", width: 150 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "rcptNo" }] },
        { cells: [{ binding: "rcptDt", type: "date" }] },
        { cells: [{ binding: "totBllTaxAmt", type: "number" }] },
        { cells: [{ binding: "rcptAmt", type: "number" }] },
        { cells: [{ binding: "rcptBnkCd", type: "text" }] },
        { cells: [{ binding: "rcptBnkAcctNo", type: "text" }] },
        { cells: [{ binding: "pyerMgmtNo", type: "text", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "pyerNm", type: "text" }] },
        { cells: [{ binding: "dcltTin", type: "text", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "dcltNm", type: "text" }] },
        { cells: [{ binding: "totAmt", type: "number" }] },
        { cells: [{ binding: "payMetdCd", area: "comnCd", comnCd: "OSC0065", type: "code" }] },
    ],
};

// Schema of Industrial Sugar Tex Receipt List Grid !== 공업용 설탕세 수납 목록 그리드 스키마 ==!
export const SG_IDST_SUGR_RCPT_LIST: TGridSchema = {
    id: "grid_idstSugrRcptList",
    options: {
        radio: false, // 라디오 버튼 보이기/숨기기 , default : false
        checkbox: false, //체크박스 보이기 숨기기, default : false
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: 120 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 140 }] },
        { cells: [{ header: "L_TANSAD_DT", binding: "tansadDt", width: 100 }] },
        { cells: [{ header: "L_RCPT_NO", binding: "rcptNo", width: 130 }] },
        { cells: [{ header: "L_RCPT_DT", binding: "rcptDt", width: 100 }] },
        { cells: [{ header: "L_TAX", binding: "totBllTaxAmt", width: 120 }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: 120 }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 100 }] },
        { cells: [{ header: "L_BLL_TAX_AMT", binding: "totAmt", width: 120 }] },
        { cells: [{ header: "L_PAY_METD", binding: "payMetdCd", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "tansadDt", type: "date" }] },
        { cells: [{ binding: "rcptNo" }] },
        { cells: [{ binding: "rcptDt", type: "date" }] },
        { cells: [{ binding: "totBllTaxAmt", type: "number" }] },
        { cells: [{ binding: "rcptAmt", type: "number" }] },
        { cells: [{ binding: "pyerMgmtNo", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "totAmt", type: "number" }] },
        { cells: [{ binding: "payMetdCd", area: "comnCd", comnCd: "OSC0065", type: "code" }] },
    ],
};

/*
 * @ Form 스키마 정의
 * @ 화면에서 사용하는 Form 구조 정의
 * @ SF_ 로 시작 (Schema form)
 * @ SF_[Form 이름 메타 대문자, "_" 로 구분 ]
 * @ 검색폼은 _SRCH 로 구분
 */

// Schema of Receipt List Search Form !== 수납 목록 검색 폼 스키마 ==!
export const SF_RCPT_LIST_SRCH: TFormSchema = {
    id: "form_rcptListSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        pyerMgmtNo: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        dcltTin: {
            type: "text",
            label: "L_DCLT_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: { name: "bllStrtDt" },
            end: { name: "bllEndDt" },
            rangeButton: 2,
            controlSize: 10,
        },
        rcptDtmRnge: {
            type: "daterange",
            label: "L_RCPT_DT",
            start: { name: "rcptStrtDt", defaultValue: comnUtils.getDate({ d: -7 }), required: true },
            end: { name: "rcptEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 2,
            controlSize: 10,
            required: true,
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        rcptBnkCd: {
            type: "code",
            label: "L_RCPT_BNK",
            area: "bnkCd",
            popupSize: "md",
        },
        rcptBnkAcctNo: {
            type: "select",
            label: "L_RCPT_BNK_ACCT",
            all: true,
        },
        payYn: {
            type: "radio",
            label: "L_PAY_YN",
            options: [
                { label: "Y", value: "Y" },
                { label: "N", value: "N" },
            ],
            all: true,
        },
        csclPlnCd: {
            type: "select",
            label: "L_CSCL_PLN",
            area: "comnCd",
            comnCd: "OSC0023",
            all: true,
        },
        /* rcptRgsrDtmRnge: {
            type: "daterange",
            // label: "Receipt Register Date",
            start: { name: "rcptRgsrStrtDt", defaultValue: comnUtils.getDate({ d: -7 }), required: true },
            end: { name: "rcptRgsrEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 2,
            controlSize: 8,
            required: true,
        },*/
        srchDtTp: {
            type: "select",
            // label: "Receipt Date Type",
            controlSize: 10,
            required: true,
            options: [
                { label: "Receipt Date", value: "rcptDtmRnge" },
                { label: "Receipt Entry Date", value: "rcptRgsrDtmRnge" },
            ],
        },
    },
};

export const SF_TOT_AMT: TFormSchema = {
    id: "form_totAmt",
    schema: {
        totAmt: {
            type: "number",
            label: "L_TOT_AMT",
        },
    },
};

// Schema of Non-Receipt List Search Form !== 미수납 목록 검색 폼 스키마 ==!
export const SF_NRCPT_LIST_SRCH: TFormSchema = {
    id: "form_nrcptListSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        pyerMgmtNo: {
            type: "code",
            label: "L_TXPR_TIN",
            area: "coCd",
            popupSize: "md",
        },
        dcltTin: {
            type: "text",
            label: "L_DCLT_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: { name: "bllStrtDt", defaultValue: comnUtils.getDate({ d: -7 }), required: true },
            end: { name: "bllEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 2,
            controlSize: 10,
            required: true,
        },
        ddlnDtmRnge: {
            type: "daterange",
            label: "L_DUE_DT",
            start: { name: "ddlnStrtDt" },
            end: { name: "ddlnEndDt" },
            rangeButton: 0,
            controlSize: 10,
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        dlqnYn: {
            type: "select",
            label: "L_STAT",
            area: "comnCd",
            comnCd: "OSC0059",
            all: true,
        },
        // Yes: bills that received a Control Number (payable). Same rule as the dashboard Unpaid Bills.
        // !== Yes 는 컨트롤번호를 받은(납부 가능한) 고지서. 대시보드 Unpaid Bills 와 같은 기준 ==!
        ctrlNoYn: {
            type: "select",
            label: "L_CTRL_NO_ISSU_YN",
            area: "comnCd",
            comnCd: "OSC0063",
            all: true,
        },
        oilDclrYn: {
            type: "text",
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
    },
};

// Schema of Receipt Error List Search Form !== 수납 오류 목록 검색 폼 스키마 ==!
export const SF_RCPT_ERR_LIST_SRCH: TFormSchema = {
    id: "form_rcptErrListSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        errRcveDtmRnge: {
            type: "daterange",
            label: "L_ERR_RCVE_DT",
            start: { name: "errRcveStrtDt", defaultValue: comnUtils.getDate({ m: -3 }), required: true },
            end: { name: "errRcveEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        oilDclrYn: {
            type: "text",
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
    },
};

// Schema of Manual Receipt List Search Form !== 수작업 수납 목록 검색 폼 스키마 ==!
export const SF_MANL_RCPT_LIST_SRCH: TFormSchema = {
    id: "form_manlRcptListSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        pyerMgmtNo: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
        rcptRgsrDtmRnge: {
            type: "daterange",
            label: "L_MANL_RCPT_RGSR_DT",
            start: { name: "rcptRgsrStrtDt", defaultValue: comnUtils.getDate({ m: -3 }), required: true },
            end: { name: "rcptRgsrEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        aprvPrcssStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0096",
        },
    },
};

// Schema of Manual Receipt Bill Search Form !== 수작업 수납 고지 검색 폼 스키마 ==!
export const SF_MANL_RCPT_SRCH: TFormSchema = {
    id: "form_manlRcptSrch",
    schema: {
        bllMgmtId: {
            label: "L_BLL_MGMT_ID",
        },
        bllNo: {
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
            minLength: 4,
            maxLength: 4,
            size: 2,
            letterCase: "upper",
            mask: [/[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/, /[A-Z0-9]/],
        },
        bllYy: {
            type: "text",
            minLength: 2,
            maxLength: 2,
            size: 1,
            mask: [/\d/, /\d/],
        },
        bllTpCd: {
            type: "text",
            minLength: 1,
            maxLength: 1,
            size: 1,
            letterCase: "upper",
            mask: [/[A-Z]/],
        },
        bllSrno: {
            type: "text",
            minLength: 7,
            maxLength: 7,
            size: 3,
            mask: [/[A-Z]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        },
        bllDgr: {
            type: "text",
            minLength: 1,
            maxLength: 2,
            size: 1,
            mask: [/\d/, /\d/],
        },
        bllRcptSrno: {
            type: "text",
            minLength: 1,
            maxLength: 2,
            size: 1,
            mask: [/\d/, /\d/],
        },
        oilDclrYn: {
            type: "text",
        },
    },
};

// Schema of Pay Discrepancy List Search Form !== 납부 불일치 목록 검색 폼 스키마 ==!
export const SF_PAY_DSCR_LIST_SRCH: TFormSchema = {
    id: "form_payDscrListSrch",
    schema: {
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: { name: "bllStrtDt", defaultValue: comnUtils.getDate({ m: -3 }), required: true },
            end: { name: "bllEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        rcptRgsrDtmRnge: {
            type: "daterange",
            label: "L_RCPT_DT",
            start: { name: "rcptStrtDt" },
            end: { name: "rcptEndDt" },
            rangeButton: 0,
            controlSize: 10,
        },
        dscrTpCd: {
            type: "select",
            label: "L_DSCR_TP",
            area: "comnCd",
            comnCd: "OSC0066",
            all: true,
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
    },
};

// Schema of Oil Tax Receipt List Search Form !== 오일세 수납 목록 검색 폼 스키마 ==!
export const SF_OIL_RCPT_LIST_SRCH: TFormSchema = {
    id: "form_oilRcptListSrch",
    schema: {
        bllCstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        pyerMgmtNo: {
            type: "code",
            label: "L_TXPR_TIN",
            area: "coCd",
            popupSize: "md",
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: { name: "bllStrtDt" },
            end: { name: "bllEndDt" },
            rangeButton: 0,
            controlSize: 10,
        },
        rcptDtmRnge: {
            type: "daterange",
            label: "L_RCPT_DT",
            start: { name: "rcptStrtDt", defaultValue: comnUtils.getDate({ m: -1 }), required: true },
            end: { name: "rcptEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 2,
            controlSize: 10,
            required: true,
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        payMetdCd: {
            type: "checkbox",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
            controlSize: 10,
            all: true,
        },
    },
};

// Schema of Industrial Sugar Tax Receipt List Search Form != 공업용 설탕세 수납 목록 검색 폼 스키마 ==!
export const SF_IDST_SUGR_RCPT_LIST_SRCH: TFormSchema = {
    id: "form_oilRcptListSrch",
    schema: {
        bllCstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        pyerMgmtNo: {
            type: "code",
            label: "L_TXPR_TIN",
            area: "coCd",
            popupSize: "md",
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllDtmRnge: {
            type: "daterange",
            label: "L_BLL_DT",
            start: { name: "bllStrtDt", defaultValue: comnUtils.getDate({ m: -3 }), required: true },
            end: { name: "bllEndDt", defaultValue: comnUtils.getDate(), required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        rcptDtmRnge: {
            type: "daterange",
            label: "L_RCPT_DT",
            start: { name: "rcptStrtDt" },
            end: { name: "rcptEndDt" },
            rangeButton: 0,
            controlSize: 10,
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        payMetdCd: {
            type: "checkbox",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
            controlSize: 10,
            all: true,
        },
    },
};

// Schema of Manual Receipt Detail Form !== 수작업 수납 상세 폼 스키마 ==!
export const SF_MANL_RCPT_DTL: TFormSchema = {
    id: "form_manlRcptDtl",
    schema: {
        bllMgmtId: {
            type: "text",
            label: "L_BLL_MGMT_ID",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
        },
        bllYy: {
            type: "text",
        },
        bllTpCd: {
            type: "text",
        },
        bllSrno: {
            type: "text",
        },
        bllDgr: {
            type: "text",
        },
        bllRcptSrno: {
            type: "text",
        },
        bllDt: {
            type: "datetime",
            label: "L_BLL_DT",
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        zmpDt: {
            type: "datetime",
            label: "L_ZMP_DT",
        },
        bllTpCdSec: {
            type: "code",
            label: "L_BLL_TP",
            area: "comnCd",
            comnCd: "OSC0058",
        },
        bllStatCd: {
            type: "code",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
        },
        pyerMgmtNoNm: {
            type: "text",
            label: "L_TXPR_TIN",
        },
        reffKey: {
            type: "text",
            label: "L_REFF_KEY",
        },
        reffKeyTpCd: {
            type: "code",
            label: "L_REFF_TP",
            area: "comnCd",
            comnCd: "OSC0067",
        },
        bllRgsrIdNm: {
            type: "text",
            label: "L_RGSR",
        },
        feeAmt: {
            type: "number",
            label: "L_FEE_AMT",
        },
        payDdlnDt: {
            type: "date",
            label: "L_DDLN_DT",
        },
        lastDdlnXtnsDt: {
            type: "date",
            label: "L_DDLN_XTNS_DT",
        },
        xtnsDt: {
            type: "number",
            label: "L_XTNS_DD",
        },
        lastDdlnXtnsRsn: {
            type: "text",
            label: "L_DDLN_XTNS_RSN",
        },
        // Manual Receipt
        payMetdCd: {
            type: "code",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
        },
        trsyVchrNo: {
            type: "text",
            label: "L_TRSY_VCHR_NO",
        },
        rcptBnkCd: {
            type: "code",
            label: "L_RCPT_BNK",
            area: "bnkCd",
        },
        rcptBnkAcctNo: {
            type: "text",
            label: "L_RCPT_BNK_ACCT_NO",
        },
        rcptNo: {
            type: "text",
            label: "L_RCPT_NO",
        },
        manlRcptRsn: {
            type: "text",
            label: "L_MANL_RCPT_RSN",
        },
        rcptAmt: {
            type: "number",
            label: "L_RCPT_AMT",
        },
        rcptDt: {
            type: "datetime",
            label: "L_RCPT_DT",
        },
        aprvPrcssStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0096",
        },
        rcptRgsrOfcrNm: {
            type: "text",
            label: "L_RGSR",
        },
        rcptRgsrDt: {
            type: "datetime",
            label: "L_RGSR_DT",
        },
        sbmrNm: {
            type: "text",
            label: "L_SBMR",
        },
        sbmsDt: {
            type: "datetime",
            label: "L_SBMS_DT",
        },
        autrNm: {
            type: "text",
            label: "L_AUTR",
        },
        audtDt: {
            type: "datetime",
            label: "L_AUDT_DT",
        },
        lastMdfrId: {
            type: "text",
        },
        lastMdfrNm: {
            type: "text",
            label: "L_CNCL_OFCR",
        },
        lastMdfyDtm: {
            type: "datetime",
            label: "L_CNCL_DT",
        },
        aprvRmrk: {
            type: "textarea",
            label: "L_RMRK",
        },
        bllPaidYn: {
            hidden: true,
        },
        dspoYn: {
            type: "text",
            hidden: true,
        },
    },
};

// Schema of Manual Receipt Save Form !== 수작업 수납 저장 폼 스키마 ==!
export const SF_MANL_RCPT_SAVE: TFormSchema = {
    id: "form_manlRcptSave",
    schema: {
        bllMgmtId: {
            type: "text",
            label: "L_BLL_MGMT_ID",
            mask: colUtils.getMaskPattern("bllNo"),
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
        },
        bllYy: {
            type: "text",
        },
        bllTpCd: {
            type: "text",
        },
        bllSrno: {
            type: "text",
        },
        bllDgr: {
            type: "text",
        },
        bllRcptSrno: {
            type: "text",
        },
        bllDt: {
            type: "datetime",
            label: "L_BLL_DT",
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        zmpDt: {
            type: "datetime",
            label: "L_ZMP_DT",
        },
        bllTpCdSec: {
            type: "code",
            label: "L_BLL_TP",
            area: "comnCd",
            comnCd: "OSC0058",
        },
        bllStatCd: {
            type: "code",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
        },
        pyerMgmtNoNm: {
            type: "text",
            label: "L_TXPR_TIN",
        },
        reffKey: {
            type: "text",
            label: "L_REFF_KEY",
        },
        reffKeyTpCd: {
            type: "code",
            label: "L_REFF_TP",
            area: "comnCd",
            comnCd: "OSC0067",
        },
        bllRgsrIdNm: {
            type: "text",
            label: "L_RGSR",
        },
        dtyAmt: {
            type: "number",
            label: "L_DTY_AMT",
        },
        pnltAmt: {
            type: "number",
            label: "L_PNLT_AMT",
        },
        vatAmt: {
            type: "number",
            label: "L_VAT_AMT",
        },
        feeAmt: {
            type: "number",
            label: "L_FEE_AMT",
        },
        othrAmt: {
            type: "number",
            label: "L_OTHR_AMT",
        },
        totBllTaxAmt: {
            type: "number",
            label: "L_TOT_BLL_TAX_AMT",
        },
        payDdlnDt: {
            type: "date",
            label: "L_DDLN_DT",
        },
        lastDdlnXtnsDt: {
            type: "date",
            label: "L_DDLN_XTNS_DT",
        },
        xtnsDt: {
            type: "number",
            label: "L_XTNS_DD",
        },
        lastDdlnXtnsRsn: {
            type: "text",
            label: "L_DDLN_XTNS_RSN",
        },
        // Manual Receipt
        payMetdCd: {
            type: "code",
            label: "L_PAY_METD",
            area: "comnCd",
            comnCd: "OSC0065",
        },
        payMetdCdSec: {
            type: "select",
            label: "L_PAY_METD",
            options: [
                { label: "Manual", value: "U" },
                { label: "Cash", value: "C" },
            ],
            required: true,
        },
        trsyVchrNo: {
            type: "text",
            label: "L_TRSY_VCHR_NO",
            required: true,
        },
        rcptBnkCd: {
            type: "code",
            label: "L_RCPT_BNK",
            area: "bnkCd",
            required: true,
        },
        rcptBnkAcctNo: {
            type: "text",
            label: "L_RCPT_BNK_ACCT_NO",
        },
        rcptNo: {
            type: "text",
            label: "L_RCPT_NO",
            required: true,
        },
        manlRcptRsn: {
            type: "text",
            label: "L_MANL_RCPT_RSN",
            required: true,
        },
        rcptAmt: {
            type: "number",
            label: "L_RCPT_AMT",
            required: true,
        },
        rcptDt: {
            type: "datetime",
            label: "L_RCPT_DT",
            required: true,
        },
        aprvStatCd: {
            type: "code",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0020",
        },
        rcptRgsrOfcrNm: {
            type: "text",
            label: "L_RGSR",
        },
        rcptRgsrDt: {
            type: "datetime",
            label: "L_RGSR_DT",
        },
        sbmrNm: {
            type: "text",
            label: "L_SBMR",
        },
        sbmsDt: {
            type: "datetime",
            label: "L_SBMS_DT",
        },
        autrNm: {
            type: "text",
            label: "L_AUTR",
        },
        audtDt: {
            type: "datetime",
            label: "L_AUDT_DT",
        },
        lastMdfrId: {
            type: "text",
        },
        lastMdfrNm: {
            type: "text",
            label: "L_CNCL_OFCR",
        },
        lastMdfyDtm: {
            type: "datetime",
            label: "L_CNCL_DT",
        },
        aprvRmrk: {
            type: "datetime",
            label: "L_RMRK",
        },
    },
};

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_RCPT_BLL_DTL_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 100 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
        { cells: [{ header: "L_CNCL_YN", binding: "dspoYn", width: 80 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
        { cells: [{ binding: "dspoYn" }] },
    ],
};
